/**
 * The pictures a post can carry — each a fact drawn, never a decoration.
 *
 * Why drawn and not generated: a picture on this site has to be true, and the
 * only way to keep it true is to draw it from the same constants the copy and
 * the app use (`shared/`, `facts.ts`). An AI photo of "a candidate at the exam"
 * says nothing and reads as stock; a bar with the threshold on it says the
 * thing the paragraph says, in a glance. These are HTML and SVG in the site's
 * own tokens, so they follow light and dark, stay sharp, and weigh nothing.
 *
 * The style is the site's: Manrope from the page, the brand green as the one
 * accent, neutrals for everything else, rounded boxes, no shadows, no
 * gradients. A figure states facts; anything invented in it (a sample score)
 * is labelled as an example on the figure itself.
 *
 * A post names a figure by key. A new figure goes here, in `FIGURES`, with its
 * words in all three languages — `tests/blog.test.ts` refuses a post that names
 * one that is not.
 */
import type { ReactNode } from "react";
import type { Locale } from "@/i18n";
import { cn } from "@/lib/utils";
import { CERTYFIKAT_LEVEL_FACTS, certyfikatVariant } from "@shared/certyfikat";
import { LISTENING_TRACK } from "@shared/listening-track";
import { LISTENING_READ_SECONDS, MODULES, SPEAKING_CRITERIA, TELC_TASKS } from "@shared/structure";
import { countWords } from "@shared/writing-review";
import { SAMPLE_STORY } from "./posts/sample-story";
import { BLOG_FACTS, CITIZENSHIP as CIT, EXAM_RULES as ER, KARTA_POLAKA as KP, LEVEL_STANDARDS as LS, STATE_AFTER_RESULT as AR, STATE_WRITING_PRINTED as WP, mmss } from "./facts";

type L<T = string> = Record<Locale, T>;

/** The frame every figure sits in: one border, one radius, one padding. */
function Frame({ title, children, example }: { title: string; children: ReactNode; example?: string }) {
  return (
    <div className="rounded-2xl border bg-card p-5 sm:p-6">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-bold tracking-tight">{title}</p>
        {example && (
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">{example}</span>
        )}
      </div>
      {children}
    </div>
  );
}

const secs = (n: number) => Math.round(n);

/* ── Listening: how many times each part is played ─────────────────────────── */

const PLAYS: { title: L; telc: L; state: L; once: L; twice: L; part: L; task: L } = {
  title: {
    pl: "Ile razy słyszysz każde nagranie",
    uk: "Скільки разів звучить кожен запис",
    ru: "Сколько раз звучит каждая запись",
    en: "How many times each recording is played",
  },
  telc: { pl: "telc B1·B2", uk: "telc B1·B2", ru: "telc B1·B2", en: "telc B1·B2" },
  state: { pl: "Państwowy", uk: "Державний", ru: "Государственный", en: "State exam" },
  once: { pl: "raz", uk: "раз", ru: "раз", en: "once" },
  twice: { pl: "dwa razy", uk: "двічі", ru: "дважды", en: "twice" },
  part: { pl: "części", uk: "частини", ru: "части", en: "parts" },
  task: { pl: "zadania", uk: "завдання", ru: "задания", en: "tasks" },
};

function Plays({ count, label }: { count: 1 | 2; label: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1">
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className={cn("h-3 flex-1 rounded-full", i === 0 ? "bg-brand" : "bg-brand/45")} />
        ))}
        {count === 1 && <span className="h-3 flex-1 rounded-full border border-dashed" />}
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

function ListeningPlays({ locale }: { locale: Locale }) {
  const t = (k: keyof typeof PLAYS) => PLAYS[k][locale];
  /* telc's listening is four parts (`TELC_TASKS.listening`), each a stretch of one track. */
  const telcParts = TELC_TASKS.listening.length;
  const rows: { name: string; unit: string; cells: { n: number; plays: 1 | 2 }[] }[] = [
    {
      name: t("telc"),
      unit: t("part"),
      cells: Array.from({ length: telcParts }, (_, i) => ({ n: i + 1, plays: 1 })),
    },
    ...(["B1", "B2"] as const).map((level) => {
      const f = BLOG_FACTS.stateListeningPlays[level];
      return {
        name: `${t("state")} ${level}`,
        unit: t("task"),
        cells: Array.from({ length: f.tasks }, (_, i) => ({
          n: i + 1,
          plays: ((f.playedOnce as readonly number[]).includes(i + 1) ? 1 : 2) as 1 | 2,
        })),
      };
    }),
  ];
  return (
    <Frame title={t("title")}>
      <div className="flex flex-col gap-5">
        {rows.map((row) => (
          <div key={row.name} className="grid gap-2 sm:grid-cols-[9rem_1fr] sm:items-start">
            <span className="text-sm font-semibold">
              {row.name} <span className="font-normal text-muted-foreground">· {row.unit}</span>
            </span>
            <div className="grid grid-cols-5 gap-2">
              {row.cells.map((c) => (
                <Plays key={c.n} count={c.plays} label={String(c.n)} />
              ))}
            </div>
          </div>
        ))}
        <p className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-brand" />{t("once")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-brand" /><span className="h-2 w-4 rounded-full bg-brand/45" />{t("twice")}</span>
        </p>
      </div>
    </Frame>
  );
}

/* ── Listening: what happens around one recording ──────────────────────────── */

const TIMELINE: { title: L; telc: L; state: L; read: L; rec: L; answer: L; signal: L; between: L; next: L; secs: L } = {
  title: {
    pl: "Co dzieje się wokół jednego nagrania",
    uk: "Що відбувається навколо одного запису",
    ru: "Что происходит вокруг одной записи",
    en: "What happens around one recording",
  },
  telc: { pl: "telc, części 1 i 2", uk: "telc, частини 1 і 2", ru: "telc, части 1 и 2", en: "telc, parts 1 and 2" },
  state: { pl: "Państwowy, zadanie odtwarzane dwa razy", uk: "Державний, завдання, що звучить двічі", ru: "Государственный, задание, которое звучит дважды", en: "State exam, a task played twice" },
  read: { pl: "czytasz pytanie", uk: "читаєш питання", ru: "читаешь вопрос", en: "read the question" },
  rec: { pl: "nagranie", uk: "запис", ru: "запись", en: "recording" },
  answer: { pl: "zaznaczasz", uk: "позначаєш", ru: "отмечаешь", en: "mark your answer" },
  signal: { pl: "sygnał", uk: "сигнал", ru: "сигнал", en: "signal" },
  between: { pl: "przerwa na odpowiedzi", uk: "пауза на відповіді", ru: "пауза на ответы", en: "pause to answer" },
  next: { pl: "drugi raz, całość", uk: "вдруге, повністю", ru: "второй раз, целиком", en: "second time, whole" },
  secs: { pl: "s", uk: "с", ru: "с", en: "s" },
};

function Segment({ grow, tone, label, sub }: { grow: number; tone: "brand" | "muted" | "line"; label: string; sub?: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1" style={{ flexGrow: grow, flexBasis: 0 }}>
      <span
        className={cn(
          "h-8 rounded-lg",
          tone === "brand" && "bg-brand",
          tone === "muted" && "bg-muted",
          tone === "line" && "border border-dashed",
        )}
      />
      <span className="truncate text-xs font-medium">{label}</span>
      {sub && <span className="text-xs tabular text-muted-foreground">{sub}</span>}
    </div>
  );
}

function ListeningTimeline({ locale }: { locale: Locale }) {
  const t = (k: keyof typeof TIMELINE) => TIMELINE[k][locale];
  const s = t("secs");
  return (
    <Frame title={t("title")}>
      <div className="flex flex-col gap-6">
        <div>
          <p className="mb-2 text-sm font-semibold">{t("telc")}</p>
          <div className="flex gap-1.5">
            <Segment grow={2} tone="muted" label={t("read")} sub={`~${secs(LISTENING_TRACK.afterLabel.read)} ${s}`} />
            <Segment grow={5} tone="brand" label={t("rec")} />
            <Segment grow={2} tone="muted" label={t("answer")} sub={`~${secs(LISTENING_TRACK.answer)} ${s}`} />
          </div>
        </div>
        <div>
          <p className="mb-2 text-sm font-semibold">{t("state")}</p>
          <div className="flex gap-1.5">
            <Segment grow={1} tone="line" label={t("signal")} />
            <Segment grow={4} tone="brand" label={t("rec")} />
            <Segment grow={4} tone="muted" label={t("between")} sub={`~${BLOG_FACTS.stateBetweenPasses} ${s}`} />
            <Segment grow={1} tone="line" label={t("signal")} />
            <Segment grow={4} tone="brand" label={t("next")} />
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ── Listening: how long the whole block runs ──────────────────────────────── */

const LENGTH: { title: L; telc: L; read3: L; read4: L; state: L; secs: L; note: L } = {
  title: { pl: "Ile trwa całe słuchanie", uk: "Скільки триває все слухання", ru: "Сколько длится всё аудирование", en: "How long the whole listening runs" },
  telc: { pl: "telc B1·B2", uk: "telc B1·B2", ru: "telc B1·B2", en: "telc B1·B2" },
  read3: { pl: "na przeczytanie pytań przed częścią 3", uk: "на читання питань перед частиною 3", ru: "на чтение вопросов перед частью 3", en: "to read the questions before part 3" },
  read4: { pl: "przed częścią 4", uk: "перед частиною 4", ru: "перед частью 4", en: "before part 4" },
  state: { pl: "Państwowy", uk: "Державний", ru: "Государственный", en: "State exam" },
  secs: { pl: "s", uk: "с", ru: "с", en: "s" },
  note: {
    pl: "Najkrótsze i najdłuższe nagranie w bibliotece",
    uk: "Найкоротший і найдовший запис у бібліотеці",
    ru: "Самая короткая и самая длинная запись в библиотеке",
    en: "Shortest and longest recording in the library",
  },
};

function ListeningLength({ locale }: { locale: Locale }) {
  const t = (k: keyof typeof LENGTH) => LENGTH[k][locale];
  const telcMinutes = MODULES.find((m) => m.id === "listening")!.minutes;
  const max = BLOG_FACTS.stateListeningSeconds.B2.max;
  /* Solid up to the shortest recording, paler up to the longest: the spread
     between sittings reads as the pale tail. */
  const bar = (from: number, to: number) => (
    <div className="relative h-3 rounded-full bg-muted">
      <span className="absolute inset-y-0 left-0 rounded-full bg-brand/40" style={{ width: `${(to / max) * 100}%` }} />
      <span className="absolute inset-y-0 left-0 rounded-full bg-brand" style={{ width: `${(from / max) * 100}%` }} />
    </div>
  );
  return (
    <Frame title={t("title")}>
      <div className="flex flex-col gap-4 text-sm">
        <div className="grid gap-1.5 sm:grid-cols-[8rem_1fr_6.5rem] sm:items-center">
          <span className="font-semibold">{t("telc")}</span>
          {bar(telcMinutes * 60, telcMinutes * 60)}
          <span className="tabular sm:text-right">{telcMinutes}:00</span>
        </div>
        {(["B1", "B2"] as const).map((level) => {
          const r = BLOG_FACTS.stateListeningSeconds[level];
          return (
            <div key={level} className="grid gap-1.5 sm:grid-cols-[8rem_1fr_6.5rem] sm:items-center">
              <span className="font-semibold">{t("state")} {level}</span>
              {bar(r.min, r.max)}
              <span className="tabular sm:text-right">{mmss(r.min)}–{mmss(r.max)}</span>
            </div>
          );
        })}
        <p className="text-xs text-muted-foreground">
          telc: {LISTENING_READ_SECONDS.interview} {t("secs")} {t("read3")}, {LISTENING_READ_SECONDS.opinions} {t("secs")} {t("read4")}. {t("note")}.
        </p>
      </div>
    </Frame>
  );
}

/* ── State exam: every part has its own bar ────────────────────────────────── */

const PER_PART: { title: L; example: L; parts: L<Record<string, string>>; pass: L; fail: L; bar: L } = {
  title: {
    pl: "Państwowy B1: każda część ma własny próg",
    uk: "Державний B1: у кожної частини свій поріг",
    ru: "Государственный B1: у каждой части свой порог",
    en: "State exam B1: every part has its own bar",
  },
  example: { pl: "przykładowy wynik", uk: "приклад результату", ru: "пример результата", en: "example result" },
  parts: {
    pl: { listening: "Słuchanie", reading: "Czytanie", elements: "Gramatyka", writing: "Pisanie", speaking: "Mówienie" },
    uk: { listening: "Слухання", reading: "Читання", elements: "Граматика", writing: "Письмо", speaking: "Говоріння" },
    ru: { listening: "Аудирование", reading: "Чтение", elements: "Грамматика", writing: "Письмо", speaking: "Говорение" },
    en: { listening: "Listening", reading: "Reading", elements: "Grammar", writing: "Writing", speaking: "Speaking" },
  },
  pass: { pl: "zdane", uk: "складено", ru: "сдано", en: "passed" },
  fail: { pl: "poniżej progu — cała sesja niezdana", uk: "нижче порога — уся сесія не складена", ru: "ниже порога — вся сессия не сдана", en: "below the bar — the whole sitting fails" },
  bar: { pl: "próg", uk: "поріг", ru: "порог", en: "bar" },
};

/** A sample candidate: strong everywhere but grammar. Labelled as an example on the figure. */
const SAMPLE_SHARE = { listening: 0.8, reading: 0.87, elements: 0.43, writing: 0.7, speaking: 0.75 } as const;

function StatePerPart({ locale }: { locale: Locale }) {
  const t = (k: Exclude<keyof typeof PER_PART, "parts">) => PER_PART[k][locale];
  const points = CERTYFIKAT_LEVEL_FACTS.B1.points;
  const pass = certyfikatVariant("B1").pass;
  const ratio = pass.kind === "telc" ? 0.5 : pass.ratio;
  return (
    <Frame title={t("title")} example={t("example")}>
      <div className="flex flex-col gap-3">
        {(Object.keys(points) as (keyof typeof points)[]).map((id) => {
          const max = points[id];
          const score = Math.round(SAMPLE_SHARE[id] * max);
          const bar = Math.ceil(ratio * max);
          const ok = score >= bar;
          return (
            <div key={id} className="grid grid-cols-[6.5rem_1fr_3.5rem] items-center gap-3 text-sm">
              <span className={cn("truncate", !ok && "font-semibold")}>{PER_PART.parts[locale][id]}</span>
              <div className="relative h-2.5 rounded-full bg-muted">
                <span
                  className={cn("absolute inset-y-0 left-0 rounded-full", ok ? "bg-brand" : "bg-destructive")}
                  style={{ width: `${(score / max) * 100}%` }}
                />
                <span className="absolute -top-1 h-[1.125rem] w-0.5 bg-foreground/70" style={{ left: `${(bar / max) * 100}%` }} aria-hidden />
              </div>
              <span className="tabular text-right">{score}/{max}</span>
            </div>
          );
        })}
        <p className="mt-1 text-xs text-muted-foreground">
          | = {t("bar")} {Math.round(ratio * 100)}% · <span className="font-semibold text-destructive">{t("fail")}</span>
        </p>
      </div>
    </Frame>
  );
}

/* ── State exam: how long the two texts are ────────────────────────────────── */

const WORDS: { title: L; short: L; long: L; words: L; note: L } = {
  title: { pl: "Ile słów mają teksty na egzaminie państwowym", uk: "Скільки слів у текстах державного іспиту", ru: "Сколько слов в текстах государственного экзамена", en: "How long the state exam's texts are" },
  short: { pl: "krótki", uk: "короткий", ru: "короткий", en: "short" },
  long: { pl: "długi", uk: "довгий", ru: "длинный", en: "long" },
  words: { pl: "słów", uk: "слів", ru: "слов", en: "words" },
  note: {
    pl: "Zakres z arkuszy w bibliotece. Wiążący jest limit wydrukowany przy zadaniu.",
    uk: "Діапазон з аркушів у бібліотеці. Обов’язковий — ліміт, надрукований біля завдання.",
    ru: "Диапазон по наборам в библиотеке. Обязателен лимит, напечатанный рядом с заданием.",
    en: "The range across the papers in the library. The limit printed by the task is the one that counts.",
  },
};

function StateWordRanges({ locale }: { locale: Locale }) {
  const t = (k: keyof typeof WORDS) => WORDS[k][locale];
  const max = 280;
  const rows = (["B1", "B2"] as const).flatMap((level) => {
    const f = CERTYFIKAT_LEVEL_FACTS[level];
    return [
      { key: `${level}s`, label: `${level} · ${t("short")}`, r: f.shortWords },
      { key: `${level}l`, label: `${level} · ${t("long")}`, r: f.longWords },
    ];
  });
  return (
    <Frame title={t("title")}>
      <div className="flex flex-col gap-3 text-sm">
        {rows.map(({ key, label, r }) => (
          <div key={key} className="grid grid-cols-[6.5rem_1fr_6.5rem] items-center gap-3">
            <span>{label}</span>
            <div className="relative h-2.5 rounded-full bg-muted">
              <span className="absolute inset-y-0 rounded-full bg-brand" style={{ left: `${(r.min / max) * 100}%`, width: `${((r.max - r.min) / max) * 100}%` }} />
            </div>
            <span className="tabular text-right">{r.min}–{r.max} {t("words")}</span>
          </div>
        ))}
        <p className="mt-1 text-xs text-muted-foreground">{t("note")}</p>
      </div>
    </Frame>
  );
}

/* ── State exam: the three things a result below the bar opens ─────────────── */

type Path = { head: string; big: string; lines: string[] };
const AFTER: { title: L; days: L; paths: L<Path[]>; note: L } = {
  title: {
    pl: "Wynik poniżej progu: trzy drogi",
    uk: "Результат нижче порога: три шляхи",
    ru: "Результат ниже порога: три пути",
    en: "A result below the bar: three ways on",
  },
  days: { pl: "dni", uk: "днів", ru: "дней", en: "days" },
  paths: {
    pl: [
      { head: "Wgląd do pracy", big: `${AR.viewCopyDays} dni`, lines: ["na wniosek do ośrodka", "dostajesz kopię swojej pracy"] },
      { head: "Ponowne sprawdzenie", big: `${AR.remarkDays} dni`, lines: [`na wniosek do Komisji, ${AR.remarkFeeEur} euro`, "tylko część pisemna", `decyzja w ${AR.remarkDecisionDays} dni, najwyżej ${AR.remarkDecisionMaxDays}`] },
      { head: "Kolejna sesja", big: "0 dni", lines: ["bez okresu oczekiwania", "cały egzamin od nowa", "i cała opłata"] },
    ],
    uk: [
      { head: "Перегляд роботи", big: `${AR.viewCopyDays} днів`, lines: ["за заявою до осередку", "отримуєш копію своєї роботи"] },
      { head: "Повторна перевірка", big: `${AR.remarkDays} днів`, lines: [`за заявою до Комісії, ${AR.remarkFeeEur} євро`, "лише письмова частина", `рішення за ${AR.remarkDecisionDays} днів, щонайбільше ${AR.remarkDecisionMaxDays}`] },
      { head: "Наступна сесія", big: "0 днів", lines: ["без періоду очікування", "увесь іспит заново", "і вся оплата"] },
    ],
    ru: [
      { head: "Просмотр работы", big: `${AR.viewCopyDays} дней`, lines: ["по заявлению в центр", "получаешь копию своей работы"] },
      { head: "Повторная проверка", big: `${AR.remarkDays} дней`, lines: [`по заявлению в Комиссию, ${AR.remarkFeeEur} евро`, "только письменная часть", `решение за ${AR.remarkDecisionDays} дней, максимум ${AR.remarkDecisionMaxDays}`] },
      { head: "Следующая сессия", big: "0 дней", lines: ["без периода ожидания", "весь экзамен заново", "и вся оплата"] },
    ],
    en: [
      { head: "See your paper", big: `${AR.viewCopyDays} days`, lines: ["on request to the centre", "you get a copy of your work"] },
      { head: "Re-marking", big: `${AR.remarkDays} days`, lines: [`on request to the Commission, €${AR.remarkFeeEur}`, "written part only", `decided in ${AR.remarkDecisionDays} days, ${AR.remarkDecisionMaxDays} at most`] },
      { head: "The next sitting", big: "0 days", lines: ["no waiting period", "the whole exam again", "and the whole fee"] },
    ],
  },
  note: {
    pl: "Terminy na wniosek liczą się od dnia, w którym dostajesz wynik na piśmie.",
    uk: "Строки на заяву рахуються від дня, коли ти отримуєш результат письмово.",
    ru: "Сроки на заявление считаются со дня, когда ты получаешь результат в письменном виде.",
    en: "The deadlines for a request run from the day you receive the result in writing.",
  },
};

function StateAfterResult({ locale }: { locale: Locale }) {
  return (
    <Frame title={AFTER.title[locale]}>
      <div className="grid gap-3 sm:grid-cols-3">
        {AFTER.paths[locale].map((p, i) => (
          <div key={p.head} className={cn("flex flex-col gap-1 rounded-xl border p-4", i === 2 && "border-brand")}>
            <span className="text-sm font-semibold">{p.head}</span>
            <span className="text-2xl font-extrabold tabular tracking-tight">{p.big}</span>
            {p.lines.map((line) => <span key={line} className="text-xs leading-relaxed text-muted-foreground">{line}</span>)}
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{AFTER.note[locale]}</p>
    </Frame>
  );
}

/* ── The story: how often it is offered, and what the brief starts you from ─── */

const STORY: { title: L; of: L; papers: L; title_: L; titlePhoto: L; first: L; photo: L; words: L } = {
  title: {
    pl: "Opowiadanie w arkuszach z sesji",
    uk: "Оповідання в наборах завдань із сесій",
    ru: "Рассказ в наборах заданий с сессий",
    en: "The story on the sittings' papers",
  },
  of: { pl: "z", uk: "з", ru: "из", en: "of" },
  papers: { pl: "arkuszy ma opowiadanie wśród długich tekstów", uk: "наборів мають оповідання серед довгих текстів", ru: "наборов предлагают рассказ среди длинных текстов", en: "papers offer a story as a long text" },
  title_: { pl: "tytuł", uk: "заголовок", ru: "заголовок", en: "a title" },
  titlePhoto: { pl: "tytuł i zdjęcie", uk: "заголовок і фото", ru: "заголовок и фото", en: "a title and a photo" },
  first: { pl: "pierwsze zdanie", uk: "перше речення", ru: "первое предложение", en: "a first sentence" },
  photo: { pl: "zdjęcie", uk: "фото", ru: "фото", en: "a photo" },
  words: { pl: "słów", uk: "слів", ru: "слов", en: "words" },
};

function StoryPrompts({ locale }: { locale: Locale }) {
  const t = (k: keyof typeof STORY) => STORY[k][locale];
  const kinds = [
    { key: "firstSentence", label: t("first") },
    { key: "title", label: t("title_") },
    { key: "titleAndPhoto", label: t("titlePhoto") },
    { key: "photo", label: t("photo") },
  ] as const;
  return (
    <Frame title={t("title")}>
      <div className="grid gap-5 sm:grid-cols-2">
        {(["B1", "B2"] as const).map((level) => {
          const f = BLOG_FACTS.stateStory[level];
          return (
            <div key={level} className="flex flex-col gap-3">
              <p className="text-sm">
                <span className="text-2xl font-extrabold tabular tracking-tight">{f.papersWithStory}</span>{" "}
                <span className="text-muted-foreground">{t("of")} {f.papers} {t("papers")} · {level}</span>
              </p>
              <div className="flex flex-col gap-1.5">
                {kinds.filter((k) => f.prompts[k.key] > 0).map((k) => (
                  <div key={k.key} className="grid grid-cols-[8.5rem_1fr_1.5rem] items-center gap-2 text-xs">
                    <span className="truncate">{k.label}</span>
                    <div className="flex gap-1">
                      {Array.from({ length: f.prompts[k.key] }, (_, i) => <span key={i} className="h-2.5 w-5 rounded-full bg-brand" />)}
                    </div>
                    <span className="tabular text-right text-muted-foreground">{f.prompts[k.key]}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {f.words.min}–{f.words.max} {t("words")}
              </p>
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

/* ── The story: one way to spend its words ─────────────────────────────────── */

const PLAN: { title: L; example: L; steps: L<string[]>; words: L; note: L } = {
  title: { pl: "Plan opowiadania z przykładu", uk: "План оповідання з прикладу", ru: "План рассказа из примера", en: "The plan of the sample story" },
  example: { pl: "propozycja, nie wymóg", uk: "пропозиція, не вимога", ru: "предложение, не требование", en: "a suggestion, not a rule" },
  steps: {
    pl: ["Początek: kto, gdzie, kiedy", "Problem", "Zwrot akcji", "Rozwiązanie", "Puenta"],
    uk: ["Початок: хто, де, коли", "Проблема", "Поворот", "Розв’язка", "Фінал"],
    ru: ["Начало: кто, где, когда", "Проблема", "Поворот", "Развязка", "Финал"],
    en: ["Opening: who, where, when", "The problem", "The turn", "The resolution", "The last line"],
  },
  words: { pl: "słów", uk: "слів", ru: "слов", en: "words" },
  note: {
    pl: "Arkusz podaje tylko długość całego tekstu. Podział jest nasz.",
    uk: "Аркуш вказує лише довжину всього тексту. Поділ — наш.",
    ru: "Набор указывает только длину всего текста. Деление — наше.",
    en: "The paper gives only the length of the whole text. The split is ours.",
  },
};
/** The split of the post's sample story, counted off its paragraphs. */
const PLAN_WORDS = SAMPLE_STORY.paragraphs.map(countWords);

function StoryPlan({ locale }: { locale: Locale }) {
  const total = PLAN_WORDS.reduce((a, b) => a + b, 0);
  return (
    <Frame title={PLAN.title[locale]} example={PLAN.example[locale]}>
      <div className="flex gap-1.5">
        {PLAN_WORDS.map((w, i) => (
          <div key={i} className="flex min-w-0 flex-col gap-1" style={{ flexGrow: w, flexBasis: 0 }}>
            <span className={cn("h-8 rounded-lg", i === 2 ? "bg-brand" : "bg-brand/40")} />
            <span className="text-xs font-medium leading-snug">{PLAN.steps[locale][i]}</span>
            <span className="text-xs tabular text-muted-foreground">~{w} {PLAN.words[locale]}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{PLAN.note[locale]} ({total} {PLAN.words[locale]})</p>
    </Frame>
  );
}

/* ── The writing block: what the paper prints over it ──────────────────────── */

const CRIT: { title: L; minutes: L; names: L<[string, string, string]>; pts: L; total: L } = {
  title: { pl: "Pisanie: co drukuje arkusz", uk: "Письмо: що друкує аркуш", ru: "Письмо: что печатает набор", en: "Writing: what the paper prints" },
  minutes: { pl: "min na oba teksty", uk: "хв на обидва тексти", ru: "мин на оба текста", en: "min for both texts" },
  names: {
    pl: ["Wykonanie zadania (treść, forma, objętość)", "Środki językowe", "Poprawność językowa"],
    uk: ["Виконання завдання (зміст, форма, обсяг)", "Мовні засоби", "Мовна правильність"],
    ru: ["Выполнение задания (содержание, форма, объём)", "Языковые средства", "Языковая правильность"],
    en: ["Task (content, form, length)", "Range of language", "Accuracy"],
  },
  pts: { pl: "p.", uk: "б.", ru: "б.", en: "pts" },
  total: { pl: "razem", uk: "разом", ru: "итого", en: "total" },
};

function StateWritingCriteria({ locale }: { locale: Locale }) {
  const t = (k: Exclude<keyof typeof CRIT, "names">) => CRIT[k][locale];
  return (
    <Frame title={t("title")}>
      <div className="grid gap-5 sm:grid-cols-2">
        {(["B1", "B2"] as const).map((level) => {
          const p = WP[level];
          const c = [p.criteria.task, p.criteria.range, p.criteria.accuracy];
          const sum = c.reduce((a, b) => a + b, 0);
          return (
            <div key={level} className="flex flex-col gap-2.5">
              <p className="text-sm">
                <span className="font-semibold">{level}</span>{" "}
                <span className="text-muted-foreground">· {p.minutes} {t("minutes")} · {t("total")} {sum} {t("pts")}</span>
              </p>
              {c.map((v, i) => (
                <div key={i} className="flex flex-col gap-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <span>{CRIT.names[locale][i]}</span>
                    <span className="tabular text-muted-foreground">0–{v}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <span className="block h-full rounded-full bg-brand" style={{ width: `${(v / 16) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </Frame>
  );
}

/* ── B1 grammar: the eight tasks, and where the points are ─────────────────── */

const GRAM: { title: L; topics: L<Record<string, string>>; forms: L<Record<string, string>>; items: L; pts: L; bar: L } = {
  title: {
    pl: "Poprawność gramatyczna B1: osiem zadań w każdym arkuszu",
    uk: "Граматика B1: вісім завдань у кожному наборі",
    ru: "Грамматика B1: восемь заданий в каждом наборе",
    en: "B1 grammar: the same eight tasks on every paper",
  },
  topics: {
    pl: { declension: "Odmiana rzeczowników, przymiotników, zaimków", conjunctions: "Spójniki", comparison: "Stopniowanie", tenses: "Czasy", questions: "Pytanie o część zdania", transformation: "Przekształcenie zdania", aspectMood: "Aspekt i tryby", prepositions: "Przyimki" },
    uk: { declension: "Відмінювання іменників, прикметників, займенників", conjunctions: "Сполучники", comparison: "Ступені порівняння", tenses: "Часи", questions: "Питання до частини речення", transformation: "Перебудова речення", aspectMood: "Вид і способи", prepositions: "Прийменники" },
    ru: { declension: "Склонение существительных, прилагательных, местоимений", conjunctions: "Союзы", comparison: "Степени сравнения", tenses: "Времена", questions: "Вопрос к части предложения", transformation: "Преобразование предложения", aspectMood: "Вид и наклонения", prepositions: "Предлоги" },
    en: { declension: "Declension of nouns, adjectives, pronouns", conjunctions: "Conjunctions", comparison: "Comparison", tenses: "Tenses", questions: "Asking about part of a sentence", transformation: "Rewriting a sentence", aspectMood: "Aspect and mood", prepositions: "Prepositions" },
  },
  forms: {
    pl: { choose: "wybór formy", bank: "wyrazy z ramki", write: "wpisujesz sam" },
    uk: { choose: "вибір форми", bank: "слова з рамки", write: "вписуєш сам" },
    ru: { choose: "выбор формы", bank: "слова из рамки", write: "вписываешь сам" },
    en: { choose: "pick a form", bank: "words from a box", write: "write it yourself" },
  },
  items: { pl: "luk", uk: "пропусків", ru: "пропусков", en: "gaps" },
  pts: { pl: "p.", uk: "б.", ru: "б.", en: "pts" },
  bar: {
    pl: "Zadania {tasks} dają {big} z {total} punktów. W zadaniu 6 każda luka jest warta punkt, w pozostałych — pół.",
    uk: "Завдання {tasks} дають {big} з {total} балів. У завданні 6 кожен пропуск коштує бал, в інших — пів бала.",
    ru: "Задания {tasks} дают {big} из {total} баллов. В задании 6 каждый пропуск стоит балл, в остальных — полбалла.",
    en: "Tasks {tasks} carry {big} of the {total} points. In task 6 each gap is worth a point, everywhere else half.",
  },
};

function GrammarB1({ locale }: { locale: Locale }) {
  const tasks = BLOG_FACTS.stateGrammarB1.tasks;
  const max = Math.max(...tasks.map((t) => t.points));
  const big = tasks.map((t) => t.points === max);
  return (
    <Frame title={GRAM.title[locale]}>
      <ol className="flex flex-col gap-2.5">
        {tasks.map((t, i) => (
          <li key={t.topic} className="grid grid-cols-[1.5rem_1fr_3.5rem] items-center gap-x-3 gap-y-1.5 text-sm">
            <span className="tabular text-muted-foreground">{i + 1}</span>
            <span className={cn("leading-snug", big[i] && "font-semibold")}>
              {GRAM.topics[locale][t.topic]}
              <span className="block text-xs font-normal text-muted-foreground">
                {GRAM.forms[locale][t.form]} · {t.items} {GRAM.items[locale]}
              </span>
            </span>
            <span className="tabular text-right">{t.points.toLocaleString(locale)} {GRAM.pts[locale]}</span>
            <div className="col-span-2 col-start-2 h-2 rounded-full bg-muted">
              <span className={cn("block h-full rounded-full", big[i] ? "bg-brand" : "bg-brand/40")} style={{ width: `${(t.points / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-xs text-muted-foreground">
        {GRAM.bar[locale]
          .replace("{tasks}", tasks.flatMap((_, i) => (big[i] ? [i + 1] : [])).join(", "))
          .replace("{big}", String(tasks.reduce((a, t, i) => a + (big[i] ? t.points : 0), 0)))
          .replace("{total}", String(tasks.reduce((a, t) => a + t.points, 0)))}
      </p>
    </Frame>
  );
}

/* ── B2: how the published answers split between TAK, NIE and BI ───────────── */

const BI: { title: L; tasks: L; perTask: L; of: L; names: L<[string, string, string]> } = {
  title: {
    pl: "Odpowiedzi w zadaniach TAK / NIE / BI na B2",
    uk: "Відповіді в завданнях TAK / NIE / BI на B2",
    ru: "Ответы в заданиях TAK / NIE / BI на B2",
    en: "Answers in the B2 TAK / NIE / BI tasks",
  },
  tasks: { pl: "zadań, po {n} zdań", uk: "завдань, по {n} тверджень", ru: "заданий, по {n} утверждений", en: "tasks of {n} statements" },
  perTask: {
    pl: "BI jest poprawne {min}–{max} razy w każdym zadaniu",
    uk: "BI правильне {min}–{max} рази в кожному завданні",
    ru: "BI — правильный ответ {min}–{max} раза в каждом задании",
    en: "BI is the answer {min}–{max} times in every task",
  },
  of: { pl: "z", uk: "з", ru: "из", en: "of" },
  names: {
    pl: ["TAK — zgodne z tekstem", "NIE — niezgodne z tekstem", "BI — brak informacji"],
    uk: ["TAK — відповідає тексту", "NIE — суперечить тексту", "BI — у тексті немає інформації"],
    ru: ["TAK — соответствует тексту", "NIE — противоречит тексту", "BI — в тексте нет информации"],
    en: ["TAK — the text says so", "NIE — the text says otherwise", "BI — the text does not say"],
  },
};

function BiSplit({ locale }: { locale: Locale }) {
  const f = BLOG_FACTS.stateBi.B2;
  const counts = [f.keys.tak, f.keys.nie, f.keys.bi];
  const total = counts.reduce((a, b) => a + b, 0);
  const tone = ["bg-brand/45", "bg-muted-foreground/35", "bg-brand"];
  return (
    <Frame title={BI.title[locale]}>
      <div className="flex h-9 overflow-hidden rounded-lg">
        {counts.map((n, i) => (
          <span key={i} className={cn("flex items-center justify-center text-xs font-bold tabular", tone[i], i === 2 && "text-brand-foreground")} style={{ width: `${(n / total) * 100}%` }}>
            {n}
          </span>
        ))}
      </div>
      <div className="mt-3 flex flex-col gap-1.5 text-xs">
        {counts.map((n, i) => (
          <span key={i} className="flex items-center gap-2">
            <span className={cn("h-2.5 w-4 rounded-full", tone[i])} />
            <span className={cn(i === 2 && "font-semibold")}>{BI.names[locale][i]}</span>
            <span className="ml-auto tabular text-muted-foreground">{n} {BI.of[locale]} {total}</span>
          </span>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">
        {f.withBi} {BI.tasks[locale].replace("{n}", String(f.statementsPerTask))} ·{" "}
        {BI.perTask[locale].replace("{min}", String(f.biPerTask.min)).replace("{max}", String(f.biPerTask.max))}
      </p>
    </Frame>
  );
}

/* ── NIE or BI: the one question that separates them ───────────────────────── */

const DECIDE: { title: L; q1: L; q2: L; no: L; yes: L; tak: L; nie: L; bi: L } = {
  title: { pl: "TAK, NIE czy BI — dwa pytania po kolei", uk: "TAK, NIE чи BI — два питання по черзі", ru: "TAK, NIE или BI — два вопроса по очереди", en: "TAK, NIE or BI — two questions in turn" },
  q1: { pl: "Czy tekst w ogóle mówi o tym, czego dotyczy zdanie?", uk: "Чи текст узагалі говорить про те, чого стосується твердження?", ru: "Говорит ли текст вообще о том, чего касается утверждение?", en: "Does the text say anything about what the statement is about?" },
  q2: { pl: "Czy mówi to samo, co zdanie?", uk: "Чи каже він те саме, що твердження?", ru: "Говорит ли он то же самое, что утверждение?", en: "Does it say the same as the statement?" },
  no: { pl: "nie", uk: "ні", ru: "нет", en: "no" },
  yes: { pl: "tak", uk: "так", ru: "да", en: "yes" },
  tak: { pl: "TAK", uk: "TAK", ru: "TAK", en: "TAK" },
  nie: { pl: "NIE", uk: "NIE", ru: "NIE", en: "NIE" },
  bi: { pl: "BI", uk: "BI", ru: "BI", en: "BI" },
};

function BiDecision({ locale }: { locale: Locale }) {
  const t = (k: keyof typeof DECIDE) => DECIDE[k][locale];
  const Answer = ({ label, strong }: { label: string; strong?: boolean }) => (
    <span className={cn("inline-flex h-9 min-w-14 items-center justify-center rounded-lg px-3 text-sm font-extrabold", strong ? "bg-brand text-brand-foreground" : "bg-muted")}>
      {label}
    </span>
  );
  const Branch = ({ word, children }: { word: string; children: ReactNode }) => (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="w-6 text-right">{word}</span>
      <span aria-hidden>→</span>
      {children}
    </div>
  );
  return (
    <Frame title={t("title")}>
      <div className="flex flex-col gap-3">
        <p className="rounded-xl border p-3 text-sm font-semibold">1. {t("q1")}</p>
        <div className="flex flex-col gap-2 pl-3">
          <Branch word={t("no")}><Answer label={t("bi")} strong /></Branch>
          <Branch word={t("yes")}><span className="text-sm font-semibold text-foreground">2. {t("q2")}</span></Branch>
          <div className="flex flex-col gap-2 pl-10">
            <Branch word={t("yes")}><Answer label={t("tak")} /></Branch>
            <Branch word={t("no")}><Answer label={t("nie")} /></Branch>
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ── telc oral: where its hundred points are ───────────────────────────────── */

const ORAL_MAP: { title: L; content: L; language: L; whole: L; sections: L<Record<string, string>>; lang: L<Record<string, string>>; pts: L } = {
  title: { pl: "Egzamin ustny telc: gdzie jest 100 punktów", uk: "Усний іспит telc: де 100 балів", ru: "Устный экзамен telc: где 100 баллов", en: "The telc oral: where its 100 points are" },
  content: { pl: "Wykonanie zadania — osobno w każdej części", uk: "Виконання завдання — окремо в кожній частині", ru: "Выполнение задания — отдельно в каждой части", en: "Task — marked in each section separately" },
  language: { pl: "Język — za całą rozmowę", uk: "Мова — за всю розмову", ru: "Язык — за весь разговор", en: "Language — for the whole conversation" },
  whole: { pl: "razem", uk: "разом", ru: "итого", en: "total" },
  sections: {
    pl: { cz1a: "1A zdjęcie", cz1b: "1B pytania", cz2a: "2A prezentacja", cz2b: "2B pytania", cz3: "3 dyskusja" },
    uk: { cz1a: "1A фото", cz1b: "1B питання", cz2a: "2A презентація", cz2b: "2B питання", cz3: "3 дискусія" },
    ru: { cz1a: "1A фото", cz1b: "1B вопросы", cz2a: "2A презентация", cz2b: "2B вопросы", cz3: "3 дискуссия" },
    en: { cz1a: "1A picture", cz1b: "1B questions", cz2a: "2A presentation", cz2b: "2B questions", cz3: "3 discussion" },
  },
  lang: {
    pl: { wymowa: "Wymowa", plynnosc: "Płynność", poprawnosc: "Poprawność", slownictwo: "Słownictwo" },
    uk: { wymowa: "Вимова", plynnosc: "Плавність", poprawnosc: "Правильність", slownictwo: "Лексика" },
    ru: { wymowa: "Произношение", plynnosc: "Беглость", poprawnosc: "Правильность", slownictwo: "Лексика" },
    en: { wymowa: "Pronunciation", plynnosc: "Fluency", poprawnosc: "Accuracy", slownictwo: "Vocabulary" },
  },
  pts: { pl: "p.", uk: "б.", ru: "б.", en: "pts" },
};

function TelcOralMap({ locale }: { locale: Locale }) {
  const m = ORAL_MAP;
  const sections = SPEAKING_CRITERIA.filter((c) => c.id.startsWith("cz"));
  const language = SPEAKING_CRITERIA.filter((c) => !c.id.startsWith("cz"));
  const sum = (xs: readonly { max: number }[]) => xs.reduce((a, c) => a + c.max, 0);
  const Row = ({ title, items, names, strong }: { title: string; items: readonly { id: string; max: number }[]; names: Record<string, string>; strong: boolean }) => (
    <div>
      <p className="mb-2 flex justify-between gap-2 text-sm font-semibold">
        <span>{title}</span>
        <span className="tabular text-muted-foreground">{sum(items)} {m.pts[locale]}</span>
      </p>
      <div className="flex gap-1.5">
        {items.map((c) => (
          <div key={c.id} className="flex min-w-0 flex-col gap-1" style={{ flexGrow: c.max, flexBasis: 0 }}>
            <span className={cn("flex h-9 items-center justify-center rounded-lg text-xs font-bold tabular", strong ? "bg-brand text-brand-foreground" : "bg-brand/40")}>
              {c.max}
            </span>
            <span className="text-xs leading-snug text-muted-foreground">{names[c.id]}</span>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <Frame title={m.title[locale]}>
      <div className="flex flex-col gap-5">
        <Row title={m.content[locale]} items={sections} names={m.sections[locale]} strong />
        <Row title={m.language[locale]} items={language} names={m.lang[locale]} strong={false} />
      </div>
    </Frame>
  );
}

/* ── A row of facts, each a figure and what it counts ──────────────────────── */

function FactCards({ title, cards, note }: { title: string; cards: { big: string; label: string }[]; note?: string }) {
  return (
    <Frame title={title}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="flex flex-col gap-1 rounded-xl border p-3">
            <span className="text-2xl font-extrabold tabular tracking-tight">{c.big}</span>
            <span className="text-xs leading-snug text-muted-foreground">{c.label}</span>
          </div>
        ))}
      </div>
      {note && <p className="mt-3 text-xs text-muted-foreground">{note}</p>}
    </Frame>
  );
}

/* ── State oral: the facts the regulation and the papers give ──────────────── */

function StateOralFacts({ locale }: { locale: Locale }) {
  const v1 = certyfikatVariant("B1");
  const pts = CERTYFIKAT_LEVEL_FACTS.B1.points.speaking;
  const bar = (l: "B1" | "B2") => {
    const p = certyfikatVariant(l).pass;
    return Math.ceil((p.kind === "telc" ? 0.5 : p.ratio) * CERTYFIKAT_LEVEL_FACTS[l].points.speaking);
  };
  const T = {
    pl: { title: "Część ustna egzaminu państwowego B1 i B2", min: "minut najwyżej", pts: "punktów za część ustną", bars: "próg na B1 / B2", when: "dni między częścią pisemną a ustną", note: "Kryteria oceny ustnej Komisja przesyła komisji egzaminacyjnej w zapieczętowanej kopercie — nie są publikowane." },
    uk: { title: "Усна частина державного іспиту B1 і B2", min: "хвилин щонайбільше", pts: "балів за усну частину", bars: "поріг на B1 / B2", when: "днів між письмовою та усною частиною", note: "Критерії оцінки усної частини Комісія надсилає екзаменаційній комісії в запечатаному конверті — їх не публікують." },
    ru: { title: "Устная часть государственного экзамена B1 и B2", min: "минут максимум", pts: "баллов за устную часть", bars: "порог на B1 / B2", when: "дней между письменной и устной частью", note: "Критерии оценки устной части Комиссия отправляет экзаменационной комиссии в запечатанном конверте — их не публикуют." },
    en: { title: "The oral part of the state exam, B1 and B2", min: "minutes at most", pts: "points for the oral", bars: "bar at B1 / B2", when: "days between the written and the oral part", note: "The Commission sends the oral criteria to the examining board in a sealed envelope — they are not published." },
  }[locale];
  return (
    <FactCards
      title={T.title}
      cards={[
        { big: String(v1.oralMinutes), label: T.min },
        { big: String(pts), label: T.pts },
        { big: `${bar("B1")} / ${bar("B2")}`, label: T.bars },
        { big: "0–1", label: T.when },
      ]}
      note={T.note}
    />
  );
}

/* ── Three ways to a certificate, and where each happens ───────────────────── */

const ROUTES: L<{ title: string; items: { head: string; where: string; who: string }[]; note: string }> = {
  pl: {
    title: "Skąd bierze się certyfikat z polskiego",
    items: [
      { head: "Egzamin państwowy", where: "W sali ośrodka uprawnionego przez Komisję, na papierze, przed komisją egzaminacyjną.", who: "Każdy, kto się zapisze." },
      { head: "Certyfikat państwowy bez egzaminu", where: "Wniosek online w systemie NAWA — to wniosek, nie egzamin.", who: "Absolwenci szkół i studiów prowadzonych po polsku, według art. 11a ust. 4 ustawy." },
      { head: "telc Język polski B1·B2", where: "W licencjonowanym ośrodku telc: wersja papierowa albo hybrydowa (pisemna na papierze, ustna przez oprogramowanie do wideokonferencji).", who: "Każdy, kto się zapisze w ośrodku." },
    ],
    note: "Test „online z certyfikatem” spoza tych trzech dróg nie jest ani certyfikatem państwowym, ani telc.",
  },
  uk: {
    title: "Звідки береться сертифікат з польської",
    items: [
      { head: "Державний іспит", where: "У залі осередку, уповноваженого Комісією, на папері, перед екзаменаційною комісією.", who: "Кожен, хто зареєструється." },
      { head: "Державний сертифікат без іспиту", where: "Заява онлайн у системі NAWA — це заява, а не іспит.", who: "Випускники шкіл і навчання польською, за ст. 11a ч. 4 закону." },
      { head: "telc Język polski B1·B2", where: "У ліцензованому центрі telc: паперова або гібридна версія (письмова на папері, усна через програму для відеоконференцій).", who: "Кожен, хто зареєструється в центрі." },
    ],
    note: "Тест «онлайн із сертифікатом» поза цими трьома шляхами — це не державний сертифікат і не telc.",
  },
  ru: {
    title: "Откуда берётся сертификат по польскому",
    items: [
      { head: "Государственный экзамен", where: "В зале центра, уполномоченного Комиссией, на бумаге, перед экзаменационной комиссией.", who: "Каждый, кто зарегистрируется." },
      { head: "Государственный сертификат без экзамена", where: "Заявление онлайн в системе NAWA — это заявление, а не экзамен.", who: "Выпускники школ и вузов с обучением на польском, по ст. 11a ч. 4 закона." },
      { head: "telc Język polski B1·B2", where: "В лицензированном центре telc: бумажная или гибридная версия (письменная на бумаге, устная через программу для видеоконференций).", who: "Каждый, кто зарегистрируется в центре." },
    ],
    note: "Тест «онлайн с сертификатом» вне этих трёх путей — это не государственный сертификат и не telc.",
  },
  en: {
    title: "Where a Polish certificate comes from",
    items: [
      { head: "The state exam", where: "In the room of a centre authorised by the Commission, on paper, before an examining board.", who: "Anyone who registers." },
      { head: "A state certificate without an exam", where: "An online application in the NAWA system — an application, not an exam.", who: "Graduates of schools and studies taught in Polish, under art. 11a(4) of the act." },
      { head: "telc Język polski B1·B2", where: "At a licensed telc centre: paper-based or hybrid (written on paper, oral through video-conference software).", who: "Anyone who registers at a centre." },
    ],
    note: "A test \"online with a certificate\" outside these three routes is neither the state certificate nor telc.",
  },
};

function CertificateRoutes({ locale }: { locale: Locale }) {
  const r = ROUTES[locale];
  return (
    <Frame title={r.title}>
      <div className="grid gap-3 sm:grid-cols-3">
        {r.items.map((it, i) => (
          <div key={it.head} className={cn("flex flex-col gap-1.5 rounded-xl border p-4", i === 1 && "border-dashed")}>
            <span className="text-sm font-bold">{it.head}</span>
            <span className="text-xs leading-relaxed">{it.where}</span>
            <span className="text-xs leading-relaxed text-muted-foreground">{it.who}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{r.note}</p>
    </Frame>
  );
}

/* ── Validity: what each certificate says about its own age ────────────────── */

function CertificateValidity({ locale }: { locale: Locale }) {
  const T = {
    pl: { title: "Jak długo ważny jest certyfikat", state: "Państwowy", telc: "telc", never: "bez terminu", stateNote: "„Certyfikat ważny jest bezterminowo” — Komisja", telcNote: "Na certyfikacie nie ma terminu ważności; o starszym decyduje instytucja — telc", dup: `duplikat w ciągu ${ER.telc.archiveYears} lat` },
    uk: { title: "Скільки дійсний сертифікат", state: "Державний", telc: "telc", never: "безстроково", stateNote: "«Сертифікат дійсний безстроково» — Комісія", telcNote: "На сертифікаті немає строку дії; про старіший вирішує установа — telc", dup: `дублікат протягом ${ER.telc.archiveYears} років` },
    ru: { title: "Сколько действителен сертификат", state: "Государственный", telc: "telc", never: "бессрочно", stateNote: "«Сертификат действителен бессрочно» — Комиссия", telcNote: "На сертификате нет срока действия; о более старом решает учреждение — telc", dup: `дубликат в течение ${ER.telc.archiveYears} лет` },
    en: { title: "How long a certificate is valid", state: "State", telc: "telc", never: "no end date", stateNote: "\"The certificate is valid indefinitely\" — the Commission", telcNote: "No validity period on the certificate; an institution decides about an older one — telc", dup: `duplicate within ${ER.telc.archiveYears} years` },
  }[locale];
  const Col = ({ name, note, extra }: { name: string; note: string; extra?: string }) => (
    <div className="flex flex-col gap-1.5 rounded-xl border p-4">
      <span className="text-sm font-bold">{name}</span>
      <span className="text-2xl font-extrabold tracking-tight">{T.never}</span>
      <span className="text-xs leading-relaxed text-muted-foreground">{note}</span>
      {extra && <span className="text-xs text-muted-foreground">{extra}</span>}
    </div>
  );
  return (
    <Frame title={T.title}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Col name={T.state} note={T.stateNote} />
        <Col name={T.telc} note={T.telcNote} extra={T.dup} />
      </div>
    </Frame>
  );
}

/* ── Exam day: what goes in with you, and what stays outside ───────────────── */

const DAY: L<{ title: string; take: string; leave: string; state: { take: string[]; leave: string[] }; telc: { take: string[]; leave: string[] } }> = {
  pl: {
    title: "Co wziąć na salę",
    take: "Weź",
    leave: "Zostaw poza salą",
    state: { take: ["dokument tożsamości ze zdjęciem", "długopis"], leave: ["telefon i każde urządzenie, które zapisuje albo przesyła informacje", "słowniki, notatki", "ołówek i korektor — nie wolno ich używać"] },
    telc: { take: ["dokument tożsamości ze zdjęciem", "ołówki, długopisy, temperówka, gumka", "napój w przezroczystej butelce bez etykiety", "chusteczki"], leave: ["kurtka, torba, telefon, zegarek — oddajesz przed egzaminem", "słowniki, podręczniki, notatki"] },
  },
  uk: {
    title: "Що взяти в залу",
    take: "Візьми",
    leave: "Залиш поза залою",
    state: { take: ["документ, що посвідчує особу, з фото", "ручку"], leave: ["телефон і будь-який пристрій, що записує або передає інформацію", "словники, нотатки", "олівець і коректор — ними не можна користуватися"] },
    telc: { take: ["документ, що посвідчує особу, з фото", "олівці, ручки, точилку, гумку", "напій у прозорій пляшці без етикетки", "серветки"], leave: ["куртку, сумку, телефон, годинник — здаєш перед іспитом", "словники, підручники, нотатки"] },
  },
  ru: {
    title: "Что взять в зал",
    take: "Возьми",
    leave: "Оставь за пределами зала",
    state: { take: ["документ, удостоверяющий личность, с фото", "ручку"], leave: ["телефон и любое устройство, которое записывает или передаёт информацию", "словари, заметки", "карандаш и корректор — ими нельзя пользоваться"] },
    telc: { take: ["документ, удостоверяющий личность, с фото", "карандаши, ручки, точилку, ластик", "напиток в прозрачной бутылке без этикетки", "салфетки"], leave: ["куртку, сумку, телефон, часы — сдаёшь перед экзаменом", "словари, учебники, заметки"] },
  },
  en: {
    title: "What to take into the room",
    take: "Take",
    leave: "Leave outside",
    state: { take: ["photo ID", "a pen"], leave: ["your phone and any device that records or sends information", "dictionaries, notes", "pencil and correction fluid — not to be used"] },
    telc: { take: ["photo ID", "pencils, pens, a sharpener, an eraser", "a drink in a clear bottle with no label", "tissues"], leave: ["jacket, bag, phone, watch — handed in before the exam", "dictionaries, textbooks, notes"] },
  },
};

const STATE_NAME: Record<Locale, string> = { pl: "Państwowy", uk: "Державний", ru: "Государственный", en: "State exam" };

function ExamDay({ locale }: { locale: Locale }) {
  const d = DAY[locale];
  const Block = ({ name, list }: { name: string; list: { take: string[]; leave: string[] } }) => (
    <div className="flex flex-col gap-2 rounded-xl border p-4">
      <span className="text-sm font-bold">{name}</span>
      <span className="text-xs font-semibold text-brand">{d.take}</span>
      <ul className="flex flex-col gap-1 text-xs">{list.take.map((x) => <li key={x}>✓ {x}</li>)}</ul>
      <span className="mt-1 text-xs font-semibold text-muted-foreground">{d.leave}</span>
      <ul className="flex flex-col gap-1 text-xs text-muted-foreground">{list.leave.map((x) => <li key={x}>✕ {x}</li>)}</ul>
    </div>
  );
  return (
    <Frame title={d.title}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Block name={STATE_NAME[locale]} list={d.state} />
        <Block name="telc" list={d.telc} />
      </div>
    </Frame>
  );
}

/* ── Numerals: which form of the noun follows which number ─────────────────── */

/*
 * A rule of Polish, not a fact counted off the papers: the noun after 1 is the
 * nominative singular, after 2–4 (and 22–24, 32–34…) the nominative plural,
 * after 5–21 (and 25–31…) the genitive plural. The words are ordinary ones.
 */
const NUM: { title: L; cols: L<[string, string, string]>; forms: L<[string, string, string]> } = {
  title: {
    pl: "Jaka forma rzeczownika stoi po liczbie",
    uk: "Яка форма іменника стоїть після числа",
    ru: "Какая форма существительного стоит после числа",
    en: "Which form of the noun follows a number",
  },
  cols: {
    pl: ["1", "2–4, 22–24…", "5–21, 25–31…"],
    uk: ["1", "2–4, 22–24…", "5–21, 25–31…"],
    ru: ["1", "2–4, 22–24…", "5–21, 25–31…"],
    en: ["1", "2–4, 22–24…", "5–21, 25–31…"],
  },
  forms: {
    pl: ["mianownik lp.", "mianownik lm.", "dopełniacz lm."],
    uk: ["називний, однина", "називний, множина", "родовий, множина"],
    ru: ["именительный, ед. ч.", "именительный, мн. ч.", "родительный, мн. ч."],
    en: ["nominative singular", "nominative plural", "genitive plural"],
  },
};
const NUM_ROWS: [string, string, string][] = [
  ["1 rok", "2 lata", "5 lat"],
  ["1 złoty", "2 złote", "5 złotych"],
  ["1 osoba", "3 osoby", "12 osób"],
  ["1 dzień", "4 dni", "21 dni"],
];

function NumeralForms({ locale }: { locale: Locale }) {
  return (
    <Frame title={NUM.title[locale]}>
      <div className="grid grid-cols-3 gap-2 text-sm">
        {NUM.cols[locale].map((c, i) => (
          <div key={c} className={cn("flex flex-col gap-0.5 rounded-xl px-3 py-2", i === 2 ? "bg-brand/15" : "bg-muted")}>
            <span className="tabular font-bold">{c}</span>
            <span className="text-xs leading-snug text-muted-foreground">{NUM.forms[locale][i]}</span>
          </div>
        ))}
        {NUM_ROWS.flatMap((row) => row.map((w, i) => (
          <span key={w} className={cn("rounded-lg border px-3 py-1.5", i === 2 && "border-brand/40")}>{w}</span>
        )))}
      </div>
    </Frame>
  );
}

/* ── Levels: how many kinds of text each level writes ──────────────────────── */

const LVL: { title: L; forms: L; spelling: L } = {
  title: {
    pl: "Ile form wypowiedzi pisemnej wymieniają przepisy",
    uk: "Скільки форм письмового висловлювання називають правила",
    ru: "Сколько форм письменного высказывания называют правила",
    en: "How many kinds of written text the standards list",
  },
  forms: { pl: "formy pisemne", uk: "письмові форми", ru: "письменные формы", en: "written forms" },
  spelling: { pl: "zasady pisowni", uk: "правила правопису", ru: "правила орфографии", en: "spelling points" },
};

function LevelForms({ locale }: { locale: Locale }) {
  const levels = ["A2", "B1", "B2"] as const;
  const max = Math.max(...levels.map((l) => LS.forms[l].length));
  return (
    <Frame title={LVL.title[locale]}>
      <ul className="flex flex-col gap-3">
        {levels.map((l) => (
          <li key={l} className="grid grid-cols-[2.5rem_1fr] items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-bold">{l}</span>
            <div className="h-3 rounded-full bg-muted">
              <span className={cn("block h-full rounded-full", l === "B2" ? "bg-brand" : "bg-brand/50")} style={{ width: `${(LS.forms[l].length / max) * 100}%` }} />
            </div>
            <span className="col-start-2 text-xs text-muted-foreground">
              {LVL.forms[locale]}: <span className="tabular font-semibold text-foreground">{LS.forms[l].length}</span> · {LVL.spelling[locale]}: <span className="tabular">{LS.spelling[l]}</span>
            </span>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

/* ── Karta Polaka: the card, the permit, the passport — and the language at each ── */

const KP_PATH: L<{ title: string; note: string; steps: { head: string; lang: string; rest: string }[] }> = {
  pl: {
    title: "Od Karty Polaka do obywatelstwa",
    note: "Według ustawy o Karcie Polaka i ustawy o obywatelstwie polskim.",
    steps: [
      { head: "Karta Polaka", lang: "przynajmniej podstawowa znajomość polskiego — w rozmowie albo z dokumentu", rest: `bezpłatnie, ważna ${KP.validYears} lat` },
      { head: "Zezwolenie na pobyt stały", lang: "Karta Polaka traci ważność z dniem jego uzyskania", rest: "wniosek do wojewody" },
      { head: "Obywatelstwo", lang: `certyfikat państwowy co najmniej ${KP.citizenshipLevel} albo świadectwo szkolne`, rest: `po co najmniej ${KP.citizenshipAfterYears} roku pobytu stałego` },
    ],
  },
  uk: {
    title: "Від Karta Polaka до громадянства",
    note: "За законом про Karta Polaka і законом про польське громадянство.",
    steps: [
      { head: "Karta Polaka", lang: "щонайменше базове знання польської — у розмові або з документа", rest: `безкоштовно, дійсна ${KP.validYears} років` },
      { head: "Дозвіл на постійне проживання", lang: "Karta Polaka втрачає чинність у день його отримання", rest: "заява до воєводи" },
      { head: "Громадянство", lang: `державний сертифікат щонайменше ${KP.citizenshipLevel} або шкільне свідоцтво`, rest: `після щонайменше ${KP.citizenshipAfterYears} року постійного проживання` },
    ],
  },
  ru: {
    title: "От Karta Polaka к гражданству",
    note: "По закону о Karta Polaka и закону о польском гражданстве.",
    steps: [
      { head: "Karta Polaka", lang: "как минимум базовое знание польского — в беседе или по документу", rest: `бесплатно, действует ${KP.validYears} лет` },
      { head: "Разрешение на постоянное проживание", lang: "Karta Polaka теряет силу в день его получения", rest: "заявление воеводе" },
      { head: "Гражданство", lang: `государственный сертификат не ниже ${KP.citizenshipLevel} или школьный аттестат`, rest: `после как минимум ${KP.citizenshipAfterYears} года постоянного проживания` },
    ],
  },
  en: {
    title: "From the Karta Polaka to citizenship",
    note: "Under the Karta Polaka Act and the Polish Citizenship Act.",
    steps: [
      { head: "Karta Polaka", lang: "at least basic Polish — shown in a conversation or by a document", rest: `free, valid for ${KP.validYears} years` },
      { head: "Permanent residence permit", lang: "the Karta Polaka lapses on the day it is granted", rest: "applied for to the voivode" },
      { head: "Citizenship", lang: `a state certificate at ${KP.citizenshipLevel} or above, or a school certificate`, rest: `after at least ${KP.citizenshipAfterYears} year of permanent residence` },
    ],
  },
};

function KartaPolakaPath({ locale }: { locale: Locale }) {
  const t = KP_PATH[locale];
  return (
    <Frame title={t.title}>
      <ol className="grid gap-3 sm:grid-cols-3">
        {t.steps.map((s, i) => (
          <li key={s.head} className={cn("flex flex-col gap-1.5 rounded-xl border p-4", i === 2 && "border-brand")}>
            <span className="text-xs font-semibold text-muted-foreground">{i + 1}</span>
            <span className="text-sm font-bold">{s.head}</span>
            <span className={cn("text-xs leading-relaxed", i === 2 && "font-semibold text-brand")}>{s.lang}</span>
            <span className="text-xs leading-relaxed text-muted-foreground">{s.rest}</span>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-xs text-muted-foreground">{t.note}</p>
    </Frame>
  );
}

/* ── Citizenship by recognition: years on a permanent permit, route by route ── */

const CIT_ROUTES: L<{ title: string; years: (n: number) => string; note: string; names: Record<(typeof CIT.routes)[number]["id"], string> }> = {
  pl: {
    title: "Ile lat pobytu wymaga każda droga uznania za obywatela",
    years: (n) => `${n} ${n === 1 ? "rok" : n < 5 ? "lata" : "lat"}`,
    note: `W każdej drodze także certyfikat państwowy co najmniej ${CIT.level} albo świadectwo szkolne (art. 30 ust. 2).`,
    names: { residence: "pobyt stały + dochód i mieszkanie", spouse: `małżeństwo z obywatelem od ${CIT.marriageYears} lat`, refugee: "status uchodźcy", long: "10 lat legalnego pobytu + dochód i mieszkanie", kartaPolaka: "Karta Polaka lub polskie pochodzenie" },
  },
  uk: {
    title: "Скільки років проживання потребує кожен шлях визнання громадянином",
    years: (n) => `${n} ${n === 1 ? "рік" : n < 5 ? "роки" : "років"}`,
    note: `На кожному шляху також державний сертифікат щонайменше ${CIT.level} або шкільне свідоцтво (ст. 30 ч. 2).`,
    names: { residence: "постійне проживання + дохід і житло", spouse: `шлюб із громадянином щонайменше ${CIT.marriageYears} роки`, refugee: "статус біженця", long: "10 років легального перебування + дохід і житло", kartaPolaka: "Karta Polaka або польське походження" },
  },
  ru: {
    title: "Сколько лет проживания требует каждый путь признания гражданином",
    years: (n) => `${n} ${n === 1 ? "год" : n < 5 ? "года" : "лет"}`,
    note: `На каждом пути также государственный сертификат не ниже ${CIT.level} или школьный аттестат (ст. 30 п. 2).`,
    names: { residence: "постоянное проживание + доход и жильё", spouse: `брак с гражданином не менее ${CIT.marriageYears} лет`, refugee: "статус беженца", long: "10 лет легального пребывания + доход и жильё", kartaPolaka: "Karta Polaka или польское происхождение" },
  },
  en: {
    title: "Years of residence each route to recognition requires",
    years: (n) => `${n} ${n === 1 ? "year" : "years"}`,
    note: `Every route also needs a state certificate at ${CIT.level} or above, or a school certificate (Art. 30(2)).`,
    names: { residence: "permanent residence + income and housing", spouse: `married to a citizen for ${CIT.marriageYears} years`, refugee: "refugee status", long: "10 years' legal residence + income and housing", kartaPolaka: "Karta Polaka or Polish descent" },
  },
};

function CitizenshipRoutes({ locale }: { locale: Locale }) {
  const t = CIT_ROUTES[locale];
  const max = Math.max(...CIT.routes.map((r) => r.years));
  return (
    <Frame title={t.title}>
      <ul className="flex flex-col gap-3">
        {CIT.routes.map((r) => (
          <li key={r.id} className="flex flex-col gap-1">
            <div className="flex items-baseline justify-between gap-3 text-xs">
              <span>{t.names[r.id]}</span>
              <span className="shrink-0 font-semibold">{t.years(r.years)}</span>
            </div>
            <div className="h-2 rounded-full bg-muted">
              <div className="h-2 rounded-full bg-brand" style={{ width: `${(r.years / max) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-muted-foreground">{t.note}</p>
    </Frame>
  );
}

export const FIGURES = {
  "citizenship-routes": CitizenshipRoutes,
  "karta-polaka-path": KartaPolakaPath,
  "level-forms": LevelForms,
  "numeral-forms": NumeralForms,
  "listening-plays": ListeningPlays,
  "listening-timeline": ListeningTimeline,
  "listening-length": ListeningLength,
  "state-per-part": StatePerPart,
  "state-word-ranges": StateWordRanges,
  "state-after-result": StateAfterResult,
  "story-prompts": StoryPrompts,
  "story-plan": StoryPlan,
  "state-writing-criteria": StateWritingCriteria,
  "grammar-b1": GrammarB1,
  "bi-split": BiSplit,
  "bi-decision": BiDecision,
  "telc-oral-map": TelcOralMap,
  "state-oral-facts": StateOralFacts,
  "certificate-routes": CertificateRoutes,
  "certificate-validity": CertificateValidity,
  "exam-day": ExamDay,
} satisfies Record<string, (props: { locale: Locale }) => ReactNode>;

export type FigureName = keyof typeof FIGURES;
